<script lang="ts">
	import Card from '@smui/card';
	import Checkbox from '@smui/checkbox';
	import Select, { Option } from '@smui/select';
	import { _ } from 'svelte-i18n';
	import { slide } from 'svelte/transition';
	import ExpandIcon from '../../../..//shared/components/common/Icons/ExpandIcon.svelte';
	import FieldLoading from '../../../..//shared/components/common/Loading/FieldLoading.svelte';
	import TableLoading from '../../../..//shared/components/common/Loading/TableLoading.svelte';
	import Pagination from '../../../..//shared/components/common/Table/Pagination.svelte';
	import Title from '../../../..//shared/components/common/Title.svelte';
	import { resetPageIfFiltersChanged } from '../../../../shared/functions';
	import AziendaEnte from '../../../../shared/models/AziendaEnte/AziendaEnte';
	import FiltroGenerico from '../../../../shared/models/Filtri/FiltroGenerico';
	import CapComuneDipendente from '../../../../shared/models/Sede/CapComuneDipendente';
	import Sede from '../../../../shared/models/Sede/Sede';
	import { hasRoleAreaManager, hasRoleSysAdmin, userSession } from '../../../../shared/stores';

	let filters: FiltroGenerico = {
		aziendaEnteId: null,
		sedeId: null,
		sediId: [],
		pagina: 1,
		elementiPerPagina: 10,
	};

	$: [data, loading, error] = CapComuneDipendente.getList(filters);

	let [dataAziendeEnti, loadingAziendeEnti, errorAziendeEnti] =
		$userSession?.roles?.some(role => ['Area Manager', 'Sys Admin'].includes(role))
			? AziendaEnte.getList({ areaId: $userSession?.areaId })
			: [null, null, null];
	$: [dataSedi, loadingSedi, errorSedi] = Sede.getList({ areaId: $userSession?.areaId, aziendaEnteId: $userSession?.aziendaEnteId || selectedAziendaEnte?.id });

	let selectedAziendaEnte: AziendaEnte = new AziendaEnte();
	let selectedSede: Sede = new Sede();

	let showRow = {};

    function toggleRow(rowId) {
        showRow[rowId] = !showRow[rowId];
    }

	let selectedSedi: number[] = [];
	let selectAll = false;
	
	function toggleSelectedSede(sedeId) {
		let tempSelectedSedi = [...selectedSedi];
		if (tempSelectedSedi.includes(sedeId)) {
			tempSelectedSedi = tempSelectedSedi.filter(id => id !== sedeId);
		} else {
			tempSelectedSedi = [...tempSelectedSedi, sedeId];
		}
		selectedSedi = tempSelectedSedi;
	}

	function toggleSelectAll() {
		let tempSelectedSedi = [...selectedSedi];
		if (selectAll) {
			tempSelectedSedi = [];
		} else {
			tempSelectedSedi = $data.response.map(row => row.sede.id);
		}
		selectedSedi = tempSelectedSedi;
		selectAll = !selectAll;
	}

	function onExport() {
		filters.sediId = selectedSedi;
		CapComuneDipendente.export(filters, $dataSedi.response);
	}

	let prevFilters = {};

	$: {
		const newPage = resetPageIfFiltersChanged(prevFilters, filters);
		if (newPage !== null) {
			filters.pagina = newPage;
		}
		prevFilters = { ...filters };
	}
</script>

<Card class="md:px-8 md:py-8" padded>
	<div class="flex justify-between">
		<Title title="{$_('page.employee.list.titleExport')}" />
		{#if !$hasRoleAreaManager}
			<div class="sm:flex-nowrap sm:space-x-4 flex flex-wrap">
				<button
					on:click="{onExport}"
					on:keydown="{(event) => {
						if (event.key === 'Enter') onExport();
					}}"
					disabled="{!selectedSedi.length}"
					class="btn-white h-14 w-14 rounded-4xl sm:h-16 sm:w-16 block mx-auto text-center cursor-pointer"
				>
					<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">file_download</span>
				</button>
			</div>
		{/if}
	</div>
	<div class="md:grid-cols-2 lg:grid-cols-3 grid grid-cols-1 gap-8 p-4">
		{#if $hasRoleSysAdmin}
			{#if $loadingAziendeEnti}
				<FieldLoading />
			{:else}
				<Select
					key="{(aziendaEnte) => `${(aziendaEnte && aziendaEnte.id) || ''}`}"
					label="{$_('page.survey.list.company/entity')}"
					bind:value="{selectedAziendaEnte}"
				>
					{#if $dataAziendeEnti && $dataAziendeEnti.response}
						<Option value="{null}" on:click="{() => (filters.aziendaEnteId = null)}">&#x200B;</Option>
						{#each $dataAziendeEnti.response as aziendaEnte (aziendaEnte.id)}
							{#if aziendaEnte?.nomeAziendaEnte}
								<Option value="{aziendaEnte}" on:click="{() => (filters.aziendaEnteId = aziendaEnte.id)}"
									>{aziendaEnte.nomeAziendaEnte}</Option
								>
							{/if}
						{/each}
					{/if}
				</Select>
			{/if}
		{/if}
		{#if $loadingSedi}
			<FieldLoading />
		{:else}
			<Select label="{$_('page.pscl.detail.location')}" key="{(sede) => `${(sede && sede.id) || ''}`}" bind:value="{selectedSede}">
				{#if $dataSedi.response}
					<Option value="{null}" on:click="{() => (filters.sedeId = null)}">&#x200B;</Option>
					{#each $dataSedi.response as sede (sede.id)}
						{#if sede.denominazione}
							<Option value="{sede}" on:click="{() => (filters.sedeId = sede.id)}">{sede.denominazione}</Option>
						{/if}
					{/each}
				{/if}
			</Select>
		{/if}
	</div>
	{#if $loading}
		<TableLoading />
	{:else}
		<div class="flex flex-col">
			<div class="sm:-mx-6 lg:-mx-8 -my-2 overflow-x-auto">
				<div class="sm:px-6 lg:px-8 inline-block min-w-full py-2 align-middle">
					<div class="overflow-hidden">
						<table class="min-w-full p-0 m-0 text-sm leading-10 border-t-4 border-collapse border-black table-auto">
							<thead>
								<tr class="p-1">
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										></th>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.locations.list.denomination')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.locations.list.localization')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.locations.list.address')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">
										<Checkbox on:change="{toggleSelectAll}" checked="{selectAll}" />
									</th>
								</tr>
							</thead>
							<tbody>
								{#if $data?.response}
									{#each $data?.response as row}
										<tr
											class="odd:bg-white even:bg-gray-50 hover:bg-gray-50 p-1"
										>
											<td class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
											><ExpandIcon condition={showRow[row.sedeId]} onClick="{() => toggleRow(row?.sedeId)}"/></td>
											<td class="border-b p-2.5 text-center leading-5">{row?.sede?.denominazione ?? ''}</td>
											<td class="border-b p-2.5 text-center leading-5">{row?.sede?.localizzazione ?? ''}</td>
											<td class="border-b p-2.5 text-center leading-5"
												>{row?.sede?.indirizzo ?? ''}{#if row?.sede?.provincia && row?.sede?.citta && row?.sede?.cap && row?.sede?.indirizzoOK}
													<span
														title="Indirizzo valido"
														class="material-icons-outlined"
														style="color: green; font-size: 20px;">fmd_good</span
													>
												{:else}
													<span
														title="Indirizzo non valido"
														class="material-icons-outlined"
														style="color: red; font-size: 20px;">wrong_location</span
													>
												{/if}</td
											>
											<td class="border-b p-2.5 text-center leading-5">
												<Checkbox on:change="{() => toggleSelectedSede(row?.sedeId)}" checked="{selectedSedi.includes(row?.sedeId)}"/>
											</td>
										</tr>
										{#if showRow[row?.sedeId]}
											<tr id="{row?.sedeId}">
												<td colspan="5" class="p-4 border">
													{#if showRow[row?.sedeId]}
														<div transition:slide>
															{#if row?.indirizzi.length > 0}
																<div class="h-48 overflow-auto">
																	<table class="min-w-full p-0 m-0 text-sm leading-10 table-fixed">
																		<thead>
																			<tr class="p-1">
																				<th scope="col" class="text-xs leading-6 tracking-wider text-center border"
																					>{$_('page.employee.list.postal_code')}</th
																				>
																				<th scope="col" class="text-xs leading-6 tracking-wider text-center border"
																					>{$_('page.employee.list.city')}</th
																				>
																				<th scope="col" class="text-xs leading-6 tracking-wider text-center border"
																					>{$_('page.employee.list.number_domiciled_employees')}</th
																				>
																			</tr>
																		</thead>
																		<tbody>
																			{#each row.indirizzi as rowIndirizzo}
																				<tr class="odd:bg-white even:bg-gray-50">
																					<td class="w-[33.33%] leading-5 text-center border">{rowIndirizzo?.cap ?? ''}</td>
																					<td class="w-[33.33%] leading-5 text-center border">{rowIndirizzo?.comune ?? ''}</td>
																					<td class="w-[33.33%] leading-5 text-center border">{rowIndirizzo?.numeroDipendentiDomiciliati ?? 0}</td
																					>
																				</tr>
																			{/each}
																		</tbody>
																	</table>
																</div>
															{:else}
																<span class="w-full p-4">Nessun indirizzo è stato trovato per questa sede</span>
															{/if}
														</div>
													{/if}
												</td>
											</tr>
										{/if}
									{/each}
								{/if}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
		<div class="right-0">
			<Pagination bind:current="{filters.pagina}" bind:num_pages="{$data.totalPages}" bind:per_page="{filters.elementiPerPagina}" />
		</div>
	{/if}
</Card>